import { useState, useEffect, useCallback } from "react"
import { useRouter } from "next/router"

/**
 * Custom hook for managing author data and pagination
 * @param {Object} initialData - Initial author data from server
 * @param {number} pageSize - Number of blogs per page
 */
export function useAuthorData(initialData, pageSize = 9) {
  const router = useRouter()
  const [authorData, setAuthorData] = useState(initialData?.authorData)
  const [isLoading, setIsLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)

  // Initialize state from URL parameters
  useEffect(() => {
    const { page } = router.query
    if (page) {
      setCurrentPage(Number(page))
    }
  }, [router.query])

  // Fetch author data when page changes
  useEffect(() => {
    const fetchAuthorData = async () => {
      if (!router.query.slug) return
      
      try {
        setIsLoading(true)
        const response = await fetch(`/api/author/${router.query.slug}?page=${currentPage}`)
        const data = await response.json()
        
        if (data.success) {
          setAuthorData(data.data)
          // Scroll to top when new data loads
          window.scrollTo({ top: 0, behavior: 'smooth' })
        } else {
          console.error('Error fetching author data:', data.error)
        }
      } catch (error) {
        console.error('Error fetching author data:', error)
      } finally {
        setIsLoading(false)
      }
    }

    // Always fetch data when page changes, including page 1
    if (currentPage !== 1) {
      fetchAuthorData()
    } else {
      // For page 1, reset to initial data
      setAuthorData(initialData?.authorData)
      // Scroll to top when returning to page 1
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [currentPage, router.query.slug, initialData?.authorData])

  // Handle page change
  const handlePageChange = useCallback((page) => {
    setCurrentPage(page)
    
    // Update URL without page refresh
    const newUrl = page === 1 
      ? `/author/${router.query.slug}` 
      : `/author/${router.query.slug}?page=${page}`
    
    router.push(newUrl, undefined, { shallow: true })
  }, [router])

  // Get author information
  const author = authorData?.data
  const blogs = author?.blogs || []
  const pagination = authorData?.meta?.pagination || {}

  return {
    authorData,
    author,
    blogs,
    pagination,
    isLoading,
    currentPage,
    handlePageChange
  }
}
